const ready = (callback) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
};

ready(() => {
  const triggers = Array.from(document.querySelectorAll('[data-category-dropdown-trigger]'));
  const finePointer = window.matchMedia?.('(hover: hover) and (pointer: fine)');
  let openTrigger = null;
  let focusOpenedTrigger = null;
  let suppressFocusOpenFor = null;
  const pointerStartedExpanded = new WeakMap();

  const panelFor = (trigger) => document.querySelector(`#${trigger.getAttribute('aria-controls')}`);

  const positionPanel = (trigger, panel) => {
    if (!panel || typeof trigger.getBoundingClientRect !== 'function') return;
    const rect = trigger.getBoundingClientRect();
    if (!panel.style) return;
    panel.style.insetInlineStart = `${Math.max(0, rect.left)}px`;
    panel.style.insetBlockStart = `${rect.bottom}px`;
  };

  const close = (trigger = openTrigger) => {
    const panel = trigger && panelFor(trigger);
    if (!trigger || !panel) return;
    trigger.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    if (openTrigger === trigger) openTrigger = null;
    if (focusOpenedTrigger === trigger) focusOpenedTrigger = null;
  };

  const open = (trigger) => {
    const panel = panelFor(trigger);
    if (!panel) return;
    if (openTrigger && openTrigger !== trigger) close(openTrigger);
    openTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    positionPanel(trigger, panel);
  };

  const isExpanded = (trigger) => trigger.getAttribute('aria-expanded') === 'true';

  const containsFocus = (trigger, next) => {
    const panel = panelFor(trigger);
    return Boolean(next && (trigger.contains(next) || panel?.contains(next)));
  };

  triggers.forEach((trigger) => {
    const panel = panelFor(trigger);
    if (!panel) return;

    trigger.addEventListener('pointerdown', () => {
      pointerStartedExpanded.set(trigger, isExpanded(trigger));
    });
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      const startedExpanded = pointerStartedExpanded.get(trigger);
      pointerStartedExpanded.delete(trigger);
      if (startedExpanded === false || focusOpenedTrigger === trigger || !isExpanded(trigger)) open(trigger);
      else close(trigger);
      focusOpenedTrigger = null;
    });
    trigger.addEventListener('focus', () => {
      if (suppressFocusOpenFor === trigger) {
        suppressFocusOpenFor = null;
        return;
      }
      const wasExpanded = isExpanded(trigger);
      open(trigger);
      if (!wasExpanded) focusOpenedTrigger = trigger;
    });
    trigger.addEventListener('focusout', (event) => {
      if (suppressFocusOpenFor === trigger) suppressFocusOpenFor = null;
      if (!containsFocus(trigger, event.relatedTarget)) close(trigger);
    });
    panel.addEventListener('focusout', (event) => {
      if (!containsFocus(trigger, event.relatedTarget)) close(trigger);
    });

    if (finePointer?.matches) {
      let closeTimer;
      const keepOpen = () => {
        window.clearTimeout(closeTimer);
        open(trigger);
      };
      const queueClose = () => {
        closeTimer = window.setTimeout(() => close(trigger), 80);
      };
      trigger.addEventListener('mouseenter', keepOpen);
      trigger.addEventListener('mouseleave', queueClose);
      panel.addEventListener('mouseenter', keepOpen);
      panel.addEventListener('mouseleave', queueClose);
    }
  });

  document.addEventListener('click', (event) => {
    if (!openTrigger) return;
    const panel = panelFor(openTrigger);
    if (openTrigger.contains(event.target) || panel?.contains(event.target)) return;
    close(openTrigger);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openTrigger) return;
    event.preventDefault();
    const trigger = openTrigger;
    const shouldSuppressFocusOpen = document.activeElement !== trigger;
    close(trigger);
    focusOpenedTrigger = null;
    if (shouldSuppressFocusOpen) suppressFocusOpenFor = trigger;
    trigger.focus({ preventScroll: true });
  });

  const reposition = () => {
    if (openTrigger) positionPanel(openTrigger, panelFor(openTrigger));
  };
  window.addEventListener('resize', reposition);
  window.addEventListener('scroll', reposition, true);
});
